import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, CalendarCheck } from "lucide-react";
import { SERVICE_LIST } from "@/data/services";

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [expOpen, setExpOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (loc.pathname !== "/") nav(`/#${id}`);
    else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header data-testid="site-header"
      className={`fixed top-0 w-full z-50 transition-colors duration-500 ${scrolled ? "backdrop-blur-2xl bg-ink/70 border-b border-white/10" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="leading-none">
          <span className="block font-serif text-xl md:text-2xl tracking-tight text-bone">Irina Ishik</span>
          <span className="block font-mono text-[9px] tracking-[0.28em] text-gold uppercase mt-0.5">Employee Relations &amp; People Risk</span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          <div className="relative" onMouseEnter={() => setExpOpen(true)} onMouseLeave={() => setExpOpen(false)}>
            <button data-testid="nav-expertise" className="flex items-center gap-1 font-sans text-xs uppercase tracking-[0.2em] text-muted2 hover:text-bone transition-colors">
              Expertise <ChevronDown size={13} />
            </button>
            {expOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                <div className="w-72 bg-elevated border border-white/12 shadow-2xl p-2">
                  {SERVICE_LIST.map((s) => (
                    <Link key={s.slug} to={`/${s.slug}`} data-testid={`nav-service-${s.slug}`}
                      className="block px-4 py-2.5 font-sans text-sm text-bone/85 hover:text-gold hover:bg-white/5 transition-colors">
                      {s.navLabel}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <Link to="/insights" data-testid="nav-insights" className="font-sans text-xs uppercase tracking-[0.2em] text-muted2 hover:text-bone transition-colors hover-underline">Insights</Link>
          <button data-testid="nav-about" onClick={() => goSection("about")} className="font-sans text-xs uppercase tracking-[0.2em] text-muted2 hover:text-bone transition-colors hover-underline">About</button>
          <button data-testid="nav-contact" onClick={() => goSection("contact")} className="font-sans text-xs uppercase tracking-[0.2em] text-muted2 hover:text-bone transition-colors hover-underline">Contact</button>
          <Link to="/book-consultation" data-testid="nav-book-consultation"
            className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] bg-gold text-ink px-5 py-2.5 hover:bg-goldhover transition-colors duration-300">
            <CalendarCheck size={14} /> Book a Consultation
          </Link>
        </nav>

        <button data-testid="mobile-menu-toggle" className="md:hidden text-bone" onClick={() => setOpen(!open)}>
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-ink/95 backdrop-blur-2xl border-b border-white/10 px-6 py-8 flex flex-col gap-5 max-h-[80vh] overflow-y-auto">
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-gold">Expertise</p>
          {SERVICE_LIST.map((s) => (
            <Link key={s.slug} to={`/${s.slug}`} onClick={() => setOpen(false)} data-testid={`nav-mobile-service-${s.slug}`}
              className="text-left font-sans text-sm text-bone/85 pl-2">{s.navLabel}</Link>
          ))}
          <div className="h-px bg-white/10 my-1" />
          <Link to="/insights" onClick={() => setOpen(false)} data-testid="nav-mobile-insights" className="font-sans text-sm uppercase tracking-[0.15em] text-muted2">Insights</Link>
          <button onClick={() => goSection("about")} data-testid="nav-mobile-about" className="text-left font-sans text-sm uppercase tracking-[0.15em] text-muted2">About</button>
          <button onClick={() => goSection("contact")} data-testid="nav-mobile-contact" className="text-left font-sans text-sm uppercase tracking-[0.15em] text-muted2">Contact</button>
          <Link to="/book-consultation" onClick={() => setOpen(false)} data-testid="nav-mobile-book"
            className="inline-flex items-center justify-center gap-2 font-sans text-xs uppercase tracking-[0.2em] bg-gold text-ink px-5 py-3.5 mt-2">
            <CalendarCheck size={14} /> Book a Consultation
          </Link>
        </div>
      )}
    </header>
  );
}
